import { FC, useEffect } from 'react';
import { Divider, Link, Stack, Typography } from '@mui/material';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { TextInput } from '../../components/forms/text-input.tsx';
import { PasswordInput } from '../../components/forms/password-input.tsx';
import { SubmitButton } from '../../components/forms/submit.button.tsx';
import { useRegisterMutation } from '../../graphql/generated/operations/auth.ts';
import { useNavigate } from 'react-router-dom';
import { Paths } from '../../constants/paths.ts';
import { useSnackbar } from '../../contexts/snackbar.context.tsx';
import { useAuth } from '../../contexts/auth.context.tsx';
import { trackEvent } from '../../utils/analytics.ts';

const validationSchema = Yup.object().shape({
  accessCode: Yup.string().required('Access Code is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().min(8, 'Password should be at least 8 characters').required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm Password is required')
});

const initialValues = {
  accessCode: '',
  email: '',
  password: '',
  confirmPassword: ''
};

export const RegisterPage: FC = () => {
  const [register, { data, error }] = useRegisterMutation();
  const { setTokens, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const { push } = useSnackbar();

  const handleLogin = async (values: typeof initialValues) => {
    trackEvent('register_attempt');
    return register({
      variables: {
        input: {
          accessCode: values.accessCode,
          email: values.email,
          password: values.password
        }
      }
    });
  };

  useEffect(() => {
    if (data?.register) {
      trackEvent('register_success');
      setTokens(data.register.accessToken, data.register.refreshToken);
      navigate(Paths.WELCOME);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      trackEvent('register_error');
      push({ message: error.message, type: 'error' });
    }
  }, [error]);

  useEffect(() => {
    if (isLoggedIn) {
      navigate(Paths.HOME);
    }
  }, [isLoggedIn]);

  return (
    <Stack gap={3}>
      <Typography variant="h3" component="h1">
        Register
      </Typography>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleLogin}>
        <Form autoComplete="on">
          <Stack gap={2}>
            <TextInput name="accessCode" label="Access Code" />
            <TextInput name="email" label="Email" autoComplete="email" type="email" />
            <PasswordInput name="password" label="Password" autoComplete="new-password" />
            <PasswordInput name="confirmPassword" label="Confirm Password" autoComplete="new-password" />
            <SubmitButton>Register</SubmitButton>
          </Stack>
        </Form>
      </Formik>
      <Divider />
      <Stack gap={1} direction="row">
        <Typography variant="body1" component="p">
          Have an account? <Link onClick={() => navigate(Paths.LOGIN)}>Login</Link>
        </Typography>
      </Stack>
    </Stack>
  );
};
